import { ComponentType, ReactNode } from 'react';

import HeroBanner from '../HeroBanner';
import SimplifiedStepSection from '../SimplifiedStepSection';
import FaqExpandedSection from '../FaqExpandedSection';
import { ImageWithText } from '../ImageWithText';
import CardImageSection from '../CardImageSection';
import TabSection from '../TabSection';
import BulletSlider from '../BulletSlider';
import { ToolSeoHead } from '../../common/ToolSeoHead';
import { IToolDefinition, ILandingExtraSection } from '../../../types/tool-definition';

interface IToolLandingPageProps {
  definition: IToolDefinition;
  /** Custom hero footer (e.g., upload button, CTA) */
  heroFooter?: ReactNode;
  /** Custom hero header (e.g., breadcrumbs) */
  heroHeader?: ReactNode;
  /** Render generic Helmet-based SEO tags. Disable when the host app emits SSR SEO tags. */
  renderSeoHead?: boolean;
  /** Children rendered between features and FAQ */
  children?: ReactNode;
}

/**
 * Section registry: maps section type strings to existing shared components.
 * Renders extra landing page sections defined in the tool definition.
 */
const EXTRA_SECTION_COMPONENTS: Partial<
  Record<ILandingExtraSection['type'], ComponentType<Record<string, unknown>>>
> = {
  'image-with-text': ImageWithText as unknown as ComponentType<Record<string, unknown>>,
  'card-image': CardImageSection as unknown as ComponentType<Record<string, unknown>>,
  'tab-section': TabSection as unknown as ComponentType<Record<string, unknown>>,
  'bullet-slider': BulletSlider as unknown as ComponentType<Record<string, unknown>>,
};

function renderExtraSection(section: ILandingExtraSection, index: number): ReactNode {
  const Component = EXTRA_SECTION_COMPONENTS[section.type];

  return Component ? <Component key={`extra-${index}`} {...section.props} /> : null;
}

/**
 * Auto-generates a complete landing page from a tool definition.
 *
 * Composes existing shared landing page components — no custom layout needed.
 * Handles SEO (meta tags + structured data), hero, features, how-to steps,
 * FAQ, and related tools sections.
 */
export default function ToolLandingPage({
  definition,
  heroFooter,
  heroHeader,
  renderSeoHead = true,
  children,
}: IToolLandingPageProps) {
  const { landing } = definition;

  return (
    <>
      {renderSeoHead && <ToolSeoHead definition={definition} />}

      {/* Hero */}
      <HeroBanner
        headerComponent={heroHeader}
        heading={landing.hero.headline}
        content={landing.hero.subheadline}
        imgSrc={landing.hero.image}
        imgAlt={`${definition.name} tool`}
        footer={heroFooter || null}
      />

      {/* How-To Steps */}
      {landing.howTo?.steps?.length > 0 && (
        <SimplifiedStepSection
          title={landing.howTo.title || `How to ${definition.name}`}
          steps={landing.howTo.steps}
          cta={null}
        />
      )}

      {/* Feature Sections */}
      {landing.features.map((feature, i) => (
        <ImageWithText
          key={`feature-${i}`}
          title={feature.title}
          description={feature.description}
          src={feature.image}
          alt={feature.imageAlt || feature.title}
          imageRight={feature.imageRight ?? i % 2 !== 0}
        />
      ))}

      {/* Extra Sections (from definition) */}
      {landing.extraSections?.map(renderExtraSection)}

      {/* Custom content slot */}
      {children}

      {/* FAQ */}
      {landing.faqs?.length > 0 && (
        <FaqExpandedSection
          title="Frequently Asked Questions"
          description=""
          questions={landing.faqs.map((faq) => ({
            question: faq.question,
            answer: faq.answer,
          }))}
        />
      )}
    </>
  );
}
